import React, { useCallback, useState } from "react";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  DatasetMetaSection,
  MetaAddButton,
} from "@/components/main/datasets/DatasetMetaSection";
import { TextInput } from "components/common/TextInput";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { updateDataset } from "@/lib/api/datasets";

/// Mirrors the server's `MAX_KEYWORDS` / `MAX_KEYWORD_LEN`.
const MAX_KEYWORDS = 20;
const MAX_KEYWORD_LEN = 60;

interface DatasetKeywordsProps {
  datasetId: string;
  keywords: string[];
  canEdit: boolean;
  onChange: (next: string[]) => void;
  pr?: string;
}

/// Freetext search keywords, deliberately NOT labels: labels are the curated
/// shared taxonomy the website filters on, these are what Dataset Search
/// matches. Both are exported; widening these is how a dataset gets found.
export const DatasetKeywords: React.FC<DatasetKeywordsProps> = ({
  datasetId,
  keywords,
  canEdit,
  onChange,
  pr,
}) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const persist = useCallback(
    async (next: string[]) => {
      setSaving(true);
      try {
        await updateDataset(datasetId, { keywords: next });
        onChange(next);
      } catch {
        showNotificationGlobal("Failed to save keywords", "error");
      } finally {
        setSaving(false);
      }
    },
    [datasetId, onChange],
  );

  const commit = useCallback(() => {
    const value = draft.trim();
    setDraft("");
    setAdding(false);
    if (!value) return;
    // Case-insensitive de-dupe, matching the server's normalization.
    if (keywords.some((k) => k.toLowerCase() === value.toLowerCase())) return;
    void persist([...keywords, value]);
  }, [draft, keywords, persist]);

  const atCap = keywords.length >= MAX_KEYWORDS;

  return (
    <DatasetMetaSection
      icon={<SellOutlinedIcon className="icon-sm" />}
      label="Keywords"
      pr={pr}
      itemCount={keywords.length}
      meta={
        keywords.length > 0 ? (
          <span
            className="text-fg-muted"
            style={{ fontSize: "var(--font-size-small)" }}
          >
            {keywords.length}
          </span>
        ) : undefined
      }
      action={
        canEdit && !adding ? (
          <MetaAddButton
            label="Add keyword"
            onClick={() => setAdding(true)}
            disabled={saving || atCap}
            title={atCap ? `At most ${MAX_KEYWORDS} keywords` : "Add a keyword"}
          />
        ) : undefined
      }
    >
      {keywords.length > 0 || adding ? (
        <div className="flex flex-wrap items-center gap-2">
          {keywords.map((k) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 min-h-[var(--control-height-sm)] px-2 rounded-app border border-border text-fg"
              style={{ fontSize: "var(--font-size-small)" }}
            >
              {k}
              {canEdit && (
                <button
                  type="button"
                  aria-label={`Remove ${k}`}
                  className="flex items-center text-fg-muted hover:text-error cursor-pointer bg-transparent border-0 p-0"
                  disabled={saving}
                  onClick={() => void persist(keywords.filter((x) => x !== k))}
                >
                  <CloseIcon className="icon-xs" />
                </button>
              )}
            </span>
          ))}
          {adding && (
            <span className="w-56">
              <TextInput
                value={draft}
                onChange={(v) => setDraft(v.slice(0, MAX_KEYWORD_LEN))}
                placeholder="e.g. model releases"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") commit();
                  if (e.key === "Escape") {
                    setDraft("");
                    setAdding(false);
                  }
                }}
                onBlur={commit}
              />
            </span>
          )}
        </div>
      ) : undefined}
    </DatasetMetaSection>
  );
};
